import { createAction, props } from '@ngrx/store';
import { ResourceData } from '../../../elements/resource/resource.types';
import { StudioProject, StudioResourceType } from './studio.reducer';
import {
  StudioScene,
  StudioSceneCell,
  StudioImage,
  StudioVideo,
  VideoInstance,
  ImageInstance,
} from './studio.types';
import { StudioTextShapeDescriptor } from './text/studio-text-shape';
import { CrudState, LoadingState } from '../../../store/store.types';

export const StudioFeature = 'studio';

// Helper function to create setter actions
function createSetterAction<T>(name: string) {
  return createAction(StudioFeature + ':' + name, props<{ value: T }>());
}

// ---------- state ------------ //

export const setStudioState = createSetterAction<
  'loading' | 'loaded' | 'error'
>('set-state');

export const setStudioError = createSetterAction<string | undefined>(
  'set-error',
);

export const setStudioCrudStateValue = createAction(
  `${StudioFeature}:set-crud-state-value`,
  props<{ key: keyof CrudState; value: LoadingState }>(),
);

// ---------- project ------------ //

// Invoked from the organisation index's loadOrganisationById effect
export const setStudioProjects = createAction(
  `${StudioFeature}:set-project-edits`,
  props<{ projects: Record<string, StudioProject> }>(),
);

export const loadProject = createAction(
  `${StudioFeature}:load-project`,
  props<{ projectID: string }>(),
);

export const loadProjectRequest = createAction(
  `${StudioFeature}:load-project-request`,
  props<{ projectID: string }>(),
);

export const setLoadedProject = createAction(
  `${StudioFeature}:set-project`,
  props<{ project?: ResourceData; scenes: Record<string, ResourceData> }>(),
);

export const setProjectLabel = createAction(
  `${StudioFeature}:set-project-label`,
  props<{ label: string }>(),
);

export const setProjectColorPalette = createAction(
  `${StudioFeature}:set-project-color-palette`,
  props<{ colorPalette: Record<string, string> }>(),
);

export const setProjectFontFamily = createAction(
  `${StudioFeature}:set-project-font-family`,
  props<{ fontFamily: string }>(),
);

export const deleteProject = createAction(
  `${StudioFeature}:delete-project`,
  props<{ projectID: string }>(),
);

export const deleteProjectSuccess = createAction(
  `${StudioFeature}:delete-project-success`,
  props<{ projectID: string }>(),
);

export const deleteProjectFailure = createAction(
  `${StudioFeature}:delete-project-failure`,
  props<{ projectID: string; error: string; code: number }>(),
);

// -------- current scene -------- //

export const saveScene = createAction(
  `${StudioFeature}:save-scene`,
  props<{
    sceneId?: string;
    value: StudioScene;
  }>(),
);

export const setCurrentStudioSceneId = createSetterAction<string>(
  'set-current-scene-id',
);

// Basic actions
export const setStudioLoading = createSetterAction<boolean>('set-loading');

export const setCurrentStudioSceneIndex = createSetterAction<number>(
  'set-current-scene-index',
);

export const resetStudio = createAction(`${StudioFeature}:reset`);

export const setStudioMode = createAction(
  `${StudioFeature}:set-mode`,
  props<{ mode: string }>(),
);

export const setStudioData = createAction(
  `${StudioFeature}:set-data`,
  props<{ data: Record<string, unknown> }>(),
);

export const deleteStudioImage = createAction(
  `${StudioFeature}:delete-image`,
  props<{ imageId: string }>(),
);

export const addStudioImage = createAction(
  `${StudioFeature}:add-image`,
  props<{ image: StudioImage }>(),
);

export const deleteStudioVideo = createAction(
  `${StudioFeature}:delete-video`,
  props<{ videoId: string }>(),
);

export const addStudioVideo = createAction(
  `${StudioFeature}:add-video`,
  props<{ video: StudioVideo }>(),
);

export const setSceneDurations = createAction(
  `${StudioFeature}:set-scene-durations`,
  props<{ durations: number[] }>(),
);

// CRUD actions
export const setStudioResourcePost = createAction(
  `${StudioFeature}:set-resource-post`,
  props<{ id: string; resourceType: StudioResourceType }>(),
);

export const setStudioResourcePatch = createAction(
  `${StudioFeature}:set-resource-patch`,
  props<{ id: string; resourceType: StudioResourceType }>(),
);

export const setStudioResourceDelete = createAction(
  `${StudioFeature}:set-resource-delete`,
  props<{ id: string; resourceType: StudioResourceType }>(),
);

export const resetStudioCrudEvents = createAction(
  `${StudioFeature}:reset-crud-events`,
);

export const saveStudioResources = createAction(
  `${StudioFeature}:save-resources`,
);

export const saveStudioResourcesSuccess = createAction(
  `${StudioFeature}:save-resources-success`,
);

export const saveStudioResourcesFailure = createAction(
  `${StudioFeature}:save-resources-failure`,
  props<{ error: string }>(),
);

// Scene descriptor actions
export const setSceneDescriptorValueByKey = createAction(
  `${StudioFeature}:set-scene-descriptor-value-by-key`,
  props<{
    sceneId: string;
    key: keyof StudioScene;
    value: StudioScene[keyof StudioScene];
  }>(),
);

// Change tracking
export const setChangeTookPlace = createAction(
  `${StudioFeature}:set-change-took-place`,
);

// Scene management
export const addStudioScene = createAction(
  `${StudioFeature}:add-scene`,
  props<{ ref: 'before' | 'after' }>(),
);

export const addLastStudioScene = createAction(
  `${StudioFeature}:add-last-scene`,
);

export const addStudioSceneBase = createAction(
  `${StudioFeature}:add-scene-base`,
  props<{ atIndex: number; scene: StudioScene }>(),
);

// Project ID
export const setCurrentStudioProjectID = createAction(
  `${StudioFeature}:set-current-project-id`,
  props<{ projectID: string }>(),
);

export const removeStudioScene = createAction(
  `${StudioFeature}:remove-scene`,
  props<{ sceneId: string }>(),
);

export const setPanelStable = createSetterAction<boolean>(
  `${StudioFeature}:set-panel-stable`,
);

export const saveCell = createAction(
  `${StudioFeature}:save-cell`,
  props<{ cellId: string; cell: StudioSceneCell }>(),
);

export const saveShape = createAction(
  `${StudioFeature}:save-shape`,
  props<{
    shapeId: string;
    shape: StudioTextShapeDescriptor | StudioTextShapeDescriptor;
  }>(),
);

export const removeText = createAction(
  `${StudioFeature}:remove-text`,
  props<{ index: number }>(),
);

export const selectCell = createAction(
  `${StudioFeature}:select-cell`,
  props<{ cellId: string }>(),
);

export const selectShape = createAction(
  `${StudioFeature}:select-shape`,
  props<{ id: string }>(),
);

export const deselectAllCells = createAction(`${StudioFeature}:deselect-cell`);

export const deselectAllShapes = createAction(
  `${StudioFeature}:deselect-shape`,
);

export const textStyleChange = createAction(
  `${StudioFeature}:text-style-change`,
  props<{
    style: Partial<{ fontFamily: string; fontSize: number; color: string }>;
  }>(),
);

export const selectMedia = createAction(
  `${StudioFeature}:select-media`,
  props<{
    id?: string;
    video?: VideoInstance;
    videoId?: string;
    image?: ImageInstance;
    videoInstanceId?: string;
  }>(),
);

export const deselectMedia = createAction(`${StudioFeature}:deselect-media`);

export const addVideoInstance = createAction(
  `${StudioFeature}:add-video-instance`,
  props<{ videoInstance: VideoInstance }>(),
);

export const updateVideoInstance = createAction(
  `${StudioFeature}:update-video-instance`,
  props<{
    videoId: string;
    videoInstanceId: string;
    updates: Partial<VideoInstance>;
  }>(),
);

export const deleteVideoInstance = createAction(
  `${StudioFeature}:delete-video-instance`,
  props<{ videoInstanceId: string; videoId: string }>(),
);

export const setShapeDescriptorAttribute = createAction(
  `${StudioFeature}:set-shape-descriptor-attribute`,
  props<{
    key: string;
    value: any;
  }>(),
);
